/**
 * useStructureHistory.js (iter-98) — In-memory undo/redo stack for
 * structural edits in Manual Compose.
 *
 * "Structural" means anything that changes the SHAPE of the chapter
 * list: add / delete / reorder / kind-change. Content typing (title,
 * subtitle, description, body) is NOT snapshotted — it would flood
 * the stack and make Ctrl+Z jump the caret around unpredictably.
 *
 * Design:
 *   - The current chapters array is the "cursor" of the timeline.
 *   - `pushSnapshot(nextChapters)` captures the pre-mutation state onto
 *     `past` and empties `future` (mutations always fork the timeline).
 *   - `undo()` pops from `past` → moves current onto `future` → sets
 *     current to the popped snapshot.
 *   - `redo()` mirrors that in reverse.
 *   - `HISTORY_LIMIT` caps the stack so a long editing session doesn't
 *     leak memory. 60 steps ≈ every structural edit for a full working
 *     session on a 40-chapter book.
 *
 * Keyboard shortcuts (Cmd/Ctrl+Z, Cmd/Ctrl+Shift+Z on Mac; Ctrl+Y on
 * Win/Linux redo) are wired directly by the consumer via
 * `bindShortcuts(target)`. This keeps the hook framework-agnostic and
 * side-effect-free by default.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const HISTORY_LIMIT = 60;

// Cheap deep clone that respects the shapes we actually store. We
// avoid `structuredClone` because it isn't available in every
// bundler target and would fail on Date / File values that could
// sneak in via legacy chapter rows.
function cloneChapters(chapters) {
  return (chapters || []).map((c) => ({ ...c }));
}

export function useStructureHistory(initialChapters) {
  // `current` mirrors the caller-owned chapters state. We keep it in a
  // ref so callbacks are stable and don't force re-renders through the
  // React tree when only the stacks change.
  const [current, setCurrent] = useState(() => cloneChapters(initialChapters));
  const currentRef = useRef(current);
  useEffect(() => { currentRef.current = current; }, [current]);

  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);

  // Consumers call this in place of `setChapters` for STRUCTURAL
  // mutations. Accepts either a next array OR an updater function.
  const commit = useCallback((updater) => {
    setPast((prevPast) => {
      const before = cloneChapters(currentRef.current);
      const nextPast = [...prevPast, before];
      // Cap the history so a heavy editing session doesn't grow
      // unbounded. Oldest entries get evicted first.
      if (nextPast.length > HISTORY_LIMIT) nextPast.shift();
      return nextPast;
    });
    setFuture([]);
    setCurrent((prev) => {
      const nextRaw = typeof updater === "function" ? updater(prev) : updater;
      return cloneChapters(nextRaw);
    });
  }, []);

  // Consumers call this for CONTENT edits (typing into title, body,
  // subtitle, etc.). No snapshot pushed.
  const replace = useCallback((updater) => {
    setCurrent((prev) => {
      const nextRaw = typeof updater === "function" ? updater(prev) : updater;
      return cloneChapters(nextRaw);
    });
  }, []);

  // Wholesale reset — used when the book document is reloaded from
  // the server (e.g. after a Save round-trip). Clears the stack so the
  // user can't undo past the last persisted state.
  const reset = useCallback((chapters) => {
    setCurrent(cloneChapters(chapters));
    setPast([]);
    setFuture([]);
  }, []);

  const undo = useCallback(() => {
    setPast((prevPast) => {
      if (prevPast.length === 0) return prevPast;
      const nextPast = prevPast.slice(0, -1);
      const restored = prevPast[prevPast.length - 1];
      setFuture((prevFuture) => [cloneChapters(currentRef.current), ...prevFuture]);
      setCurrent(cloneChapters(restored));
      return nextPast;
    });
  }, []);

  const redo = useCallback(() => {
    setFuture((prevFuture) => {
      if (prevFuture.length === 0) return prevFuture;
      const [restored, ...rest] = prevFuture;
      setPast((prevPast) => {
        const next = [...prevPast, cloneChapters(currentRef.current)];
        if (next.length > HISTORY_LIMIT) next.shift();
        return next;
      });
      setCurrent(cloneChapters(restored));
      return rest;
    });
  }, []);

  const canUndo = past.length > 0;
  const canRedo = future.length > 0;

  // Cmd/Ctrl+Z (undo) and Cmd/Ctrl+Shift+Z or Ctrl+Y (redo). Bound
  // to `window` because chapter cards + rich editors capture their
  // own key events; we want structural undo to still work while the
  // author is in a content field. We guard against text-input undo
  // by first checking whether the active element already handled
  // the shortcut natively (isContentEditable / textarea / input).
  const bindShortcuts = useCallback((target = window) => {
    const onKey = (e) => {
      const cmd = e.metaKey || e.ctrlKey;
      if (!cmd) return;
      // Let native input/contenteditable undo run first — structural
      // undo is only invoked when focus is OUTSIDE an editable field.
      const el = document.activeElement;
      const isEditable = el && (
        el.tagName === "INPUT" || el.tagName === "TEXTAREA" ||
        el.isContentEditable
      );
      if (isEditable) return;
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        redo();
      }
    };
    target.addEventListener("keydown", onKey);
    return () => target.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  return useMemo(() => ({
    chapters: current,
    commit, replace, reset,
    undo, redo, canUndo, canRedo,
    past, future,
    bindShortcuts,
  }), [current, commit, replace, reset, undo, redo, canUndo, canRedo, past, future, bindShortcuts]);
}
